import { breakpoints } from 'app/shared'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { css } from 'styled-components'

import { styled, theme } from 'ui/styles'

interface WrapperProps {
  backroundTheme?: 'light' | 'dark'
  centerContent?: boolean
}

const centerContentStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
`

const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  width: 100%;
  height: 100%;
  padding: 50px 0;
  top: 0;
  left: 0;
  background: ${(props) => (props.backroundTheme === 'light' ? 'rgba(255, 255, 255, 0.64)' : 'rgba(20, 31, 62, 0.7)')};

  z-index: 9999; // todo
  overflow: auto;

  ${(props) => (props.centerContent ? centerContentStyles : '')}
  
  @media (max-width: ${breakpoints.desktop - 1}px) {
    padding: 64px 0;
  }

  @media (max-width: ${breakpoints.mobile - 1}px) {
    overflow-x: hidden;
    padding: 16px;
  }
`

const Content = styled.div<{ maxWidth: string }>`
  position: relative;
  max-width: ${({ maxWidth }) => maxWidth};
  background-color: white;
  border-radius: 16px;
  box-shadow: 0px 16px 32px rgba(42, 61, 87, 0.16);
  padding: 32px 32px 40px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: scale(0);
  transition: opacity, transform, ${theme.transition.hover}ms ease;

  &.open {
    opacity: 1;
    transform: scale(1);
  }

  @media (max-width: ${breakpoints.mobile - 1}px) {
    overflow-y: scroll;
    padding: 16px 16px 24px !important;
    opacity: 1;
  }
`

type ModalProps = {
  isVisible: boolean
  contentMaxWidth?: string
  onOverlayClick?: () => void
} & WrapperProps

export const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  isVisible,
  contentMaxWidth = '90%',
  onOverlayClick,
  backroundTheme = 'dark',
  centerContent,
  children
}) => {
  const portal = document.getElementById('modal')
  const root = document.getElementById('root')
  const body = document.body

  const [isOpening, setIsOpening] = useState(isVisible)

  const isOpen = React.useMemo(() => !!portal && isVisible, [portal, isVisible])

  useEffect(() => {
    setIsOpening(isVisible)
  }, [isVisible])

  useEffect(() => {
    if (isOpen) {
      root?.classList.add('blur')
      body.classList.add('overflow-hidden')
    } else {
      root?.classList.remove('blur')
      body.classList.remove('overflow-hidden')
    }
  }, [isOpen, root, body])

  if (!isOpen) {
    return null
  }

  return ReactDOM.createPortal(
    <Wrapper centerContent={centerContent} backroundTheme={backroundTheme} onClick={onOverlayClick}>
      <Content
        maxWidth={contentMaxWidth}
        className={isOpening ? 'open' : ''}
        onClick={(ev) => {
          ev.stopPropagation()
        }}
      >
        {children}
      </Content>
    </Wrapper>,
    portal!
  )
}
